import distance from '@turf/distance';
import { point, Units } from '@turf/helpers';
import { AqiStandardV2Dto, DatasourceId, Point, SensedConfigDto, SourceType } from '../types';
import { isPositiveStandard } from './aqi';
import { EARTH_CIRCUMFERENCE_KM, MAX_MAP_ZOOM, MIN_MAP_ZOOM, VECTOR_TILE_SIZE, ZOOM_PRECISION } from './constants';
import { MapData } from './map-data';

export const isInBoundingBox = (bounds: number[][], coordinates: [number, number]): boolean => {
  const [minLng, minLat] = bounds[0];
  const [maxLng, maxLat] = bounds[1];
  const [lng, lat] = coordinates;
  return lng >= minLng && lng <= maxLng && lat >= minLat && lat <= maxLat;
};

export const computeDistance = (
  p1Coords: Point['coordinates'],
  p2Coords: Point['coordinates'],
  units: Units
): number => {
  const from = point(p1Coords);
  const to = point(p2Coords);
  return distance(from, to, { units });
};

export function computeDistancesFromCoords(
  fromCoords: Point['coordinates'],
  toCoords: Point['coordinates'][],
  units: Units
): number[] {
  return toCoords.map((coords) => {
    return computeDistance(fromCoords, coords, units);
  });
}

export function findNearestDatasourceId(coords: Point['coordinates'], mapData: MapData[]): [DatasourceId, number] {
  const distances = computeDistancesFromCoords(
    coords,
    mapData.map((m) => m.position),
    'meters'
  );
  const nearestIndex = distances.indexOf(Math.min(...distances));
  return [mapData[nearestIndex].datasourceId, distances[nearestIndex]];
}

export function findWorstPollutedDatasourceId(
  siteCoords: Point['coordinates'],
  mapData: MapData[],
  radiusInMeters: number,
  sensedConfigs: SensedConfigDto[],
  aqiStandard: AqiStandardV2Dto
): DatasourceId | null {
  if (!mapData || !sensedConfigs || !aqiStandard) {
    return null;
  }
  // Calculate distances to the target location
  // TODO: maybe use the value in settings instead of calculating it again
  const nodeMarkers = mapData.filter((m) => m.marker.sourceType === SourceType.CLARITY_NODE);
  const distances = computeDistancesFromCoords(
    siteCoords,
    nodeMarkers.map((m) => m.position),
    'meters'
  );

  // Find datasources within radius
  const withinRadiusMapDataWithIndex = nodeMarkers
    .map<[MapData, number]>((data, index) => [data, index])
    .filter(([, index]) => distances[index] <= radiusInMeters);

  if (withinRadiusMapDataWithIndex.length === 0) {
    return null;
  }

  const nearbyDatasources = withinRadiusMapDataWithIndex.map(([data]) => data);

  // Choose the datasource with the poorest AQI
  const pollutantConfigs = sensedConfigs.filter((config) => config.isPollutant);
  const aqiValues = nearbyDatasources
    .map((data, index) => [data.getMainPollutantAqiValue(pollutantConfigs, aqiStandard), index])
    .filter(([aqi]) => aqi !== null && aqi !== undefined) as [number, number][];

  const comparisonFunction = isPositiveStandard(aqiStandard.aqiStandardId) ? Math.max : Math.min;
  const [, chosenIndex] = aqiValues.reduce(([bestAqi, bestIndex], [currentAqi, currentIndex]) => {
    if (Math.round(bestAqi) === Math.round(currentAqi)) {
      // Choose closest datasource if AQI is the same
      const bestCoords = nearbyDatasources[bestIndex].position;
      const currentCoords = nearbyDatasources[currentIndex].position;
      const bestDistance = computeDistance(siteCoords, bestCoords, 'meters');
      const currentDistance = computeDistance(siteCoords, currentCoords, 'meters');
      return bestDistance < currentDistance ? [bestAqi, bestIndex] : [currentAqi, currentIndex];
    }
    return comparisonFunction(bestAqi, currentAqi) === bestAqi ? [bestAqi, bestIndex] : [currentAqi, currentIndex];
  });
  return nearbyDatasources[chosenIndex].datasourceId;
}

/**
 * Calculate the optimal zoom level to ensure a circular radius around a center point
 * remains visible within the map viewport, accounting for Web Mercator projection.
 */
export function getZoomForRadius(
  distanceInMeters: number,
  containerWidthPx: number,
  containerHeightPx: number,
  latitude: number,
  paddingPx: number = 20
): number {
  // STEP 1: Calculate usable viewport space
  // Use the smaller dimension to ensure radius fits in both width and height
  // Subtract padding from both sides (2 * paddingPx)
  const usablePx = Math.max(1, Math.min(containerWidthPx, containerHeightPx) - 2 * paddingPx);

  // STEP 2: Convert to radius constraint
  // For a center point, the maximum radius that fits is half the usable space
  // This ensures any point at distanceKm from center remains visible
  const radiusPx = usablePx / 2;

  // STEP 3: Calculate required resolution
  const metersPerPixelWanted = distanceInMeters / radiusPx;

  // STEP 4: Web Mercator projection math
  // At zoom 0, the entire Earth's equator circumference fits in tileSize pixels
  // This gives us the baseline meters-per-pixel at the equator
  const equatorMPP = (EARTH_CIRCUMFERENCE_KM * 1000) / VECTOR_TILE_SIZE;

  // As you move away from the equator toward the poles,
  // the meridians (longitude lines) get closer together.
  //
  // Think of a right triangle where:
  // - Hypotenuse = Earth's radius (R)
  // - Angle = latitude (φ)
  // - Adjacent side = horizontal distance from Earth's axis
  //
  // By trigonometry: horizontal distance = R × cos(φ)
  //
  // This means:
  // - At equator (φ = 0°): cos(0°) = 1, full horizontal scale
  // - At 60° latitude: cos(60°) = 0.5, horizontal distances are 50% of equatorial
  // - At poles (φ = 90°): cos(90°) = 0, meridians converge to a point
  //
  // Web Mercator projection flattens this sphere onto a rectangular map, but
  // preserves the cos(latitude) relationship for horizontal scaling.
  const latRadians = (latitude * Math.PI) / 180;
  const latFactor = Math.cos(latRadians);

  // Zoom level calculation:
  // - At zoom z: meters-per-pixel = (equatorMPP × latFactor) / 2^z
  // - Solving for z: 2^z = (equatorMPP × latFactor) / metersPerPixelWanted
  // - Therefore: z = log₂((equatorMPP × latFactor) / metersPerPixelWanted)
  const exactZoom = Math.log2((equatorMPP * latFactor) / metersPerPixelWanted);

  // STEP 5: Apply precision and bounds
  // Round to specified precision to avoid floating point artifacts
  const roundedZoom = Math.round(exactZoom * 10 ** ZOOM_PRECISION) / 10 ** ZOOM_PRECISION;

  // Clamp to valid zoom range for the map
  return Math.max(MIN_MAP_ZOOM, Math.min(MAX_MAP_ZOOM, roundedZoom));
}
